import { Link } from 'react-router';
import { 
  GuideLayout, 
  GuideSection, 
  GuideTip, 
  GuideGrid,
  GuideCard,
  GuideDarkSection,
  SeverityLevel
} from '@/components/guides/guide-layout';

const inlineLink =
  'text-brand-600 hover:text-brand-700 font-medium underline decoration-brand-300 underline-offset-2';

export function UnderstandingRejectionReasonsGuide() {
  return (
    <GuideLayout
      slug="understanding-rejection-reasons"
      category="Escalation"
      categoryColor="warning"
      title="Common Rejection Reasons (And How to Respond)"
      description="Why councils reject pothole claims and what you can do about each type of rejection."
      readTime="6 min"
      updatedDate="2026-01-12"
      datePublished="2026-01-10"
      reviewedFor="UK law"
      relatedGuides={[
        { slug: 'section-58-defence', title: 'The Section 58 Defence', readTime: '7 min' },
        { slug: 'small-claims-court-guide', title: 'Small Claims Court Guide', readTime: '10 min' },
        { slug: 'prior-reports-explained', title: 'Why Prior Reports Matter', readTime: '5 min' },
      ]}
      atAGlance={[
        { label: 'Most common rejection', value: '"Our inspection systems are reasonable" — the Section 58 defence' },
        { label: 'First step after rejection', value: "Request the council's inspection records, prior reports data, and the specific basis for rejection" },
        { label: 'Deadline to set', value: '14-28 days for the council to reconsider before you proceed to court' },
        { label: 'Escalation route', value: 'Small claims court — fees range from £35-£115 depending on claim amount' },
      ]}
      faqs={[
        {
          question: 'Should I accept a rejection?',
          answer: 'Not necessarily. Many rejections are based on generic template responses that may not apply to your specific case. Read the rejection carefully and consider whether the reasons given actually match your situation.',
        },
        {
          question: 'Can I appeal a council rejection?',
          answer: 'Councils don\'t have a formal appeals process for pothole claims, but you can respond challenging their reasons. If that fails, your recourse is small claims court — which is designed for exactly this purpose.',
        },
        {
          question: 'How often do councils settle after court claim is filed?',
          answer: 'Many councils settle once a court claim is actually filed, often before the hearing date. The cost and effort of defending in court often exceeds the claim value, so they pay to make it go away.',
        },
        {
          question: 'What if they say the pothole wasn\'t dangerous enough?',
          answer: 'If the pothole caused damage to your vehicle, it was dangerous enough. Councils often set arbitrary "intervention levels" (e.g., 40mm depth), but these are policy guidelines, not legal thresholds.',
        },
        {
          question: 'What if they say I should have seen it and avoided it?',
          answer: 'This is called "contributory negligence" and is rarely a complete defence. At most, it might reduce your compensation slightly. Drivers are entitled to expect roads to be safe.',
        },
        {
          question: 'What\'s the cost of going to small claims court?',
          answer: 'Court fees range from £35-£115 depending on your claim amount. If you win, the court adds this to what the council must pay. Many councils settle before the hearing to avoid the hassle.',
        },
      ]}
    >
      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        }
        title="Don't Give Up Yet"
      >
        <p className="mb-4">
          Getting a rejection letter is frustrating, but it's often just the council's first response. 
          Many rejections use template language — often leaning on{' '}
          <Link to="/guides/section-58-defence" className={inlineLink}>
            the Section 58 defence
          </Link>
          {' '}— that doesn't hold up to scrutiny. Understanding why 
          they rejected you helps you decide whether to push back.
        </p>
        <GuideTip variant="success" title="Remember">
          The council hopes you'll give up. Many people do. Those who persist often succeed — either 
          by challenging the rejection or escalating to court.
        </GuideTip>
      </GuideSection>

      <GuideDarkSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        }
        title="Common Rejection Reasons"
      >
        <div className="space-y-4">
          <SeverityLevel level="high" badge="Common">
            <strong>"Our inspection systems are reasonable."</strong> They claim Section 58 defence — 
            challenge by requesting their inspection records and checking for prior reports.
          </SeverityLevel>

          <SeverityLevel level="high" badge="Common">
            <strong>"The defect didn't meet our intervention criteria."</strong> They say the pothole 
            was too small. Counter: if it caused damage, it was dangerous. Their criteria are policy, not law.
          </SeverityLevel>

          <SeverityLevel level="medium" badge="Sometimes valid">
            <strong>"This is a private road."</strong> If true, you'd need to claim against the landowner. 
            If false, challenge with evidence the road is publicly maintained.
          </SeverityLevel>

          <SeverityLevel level="medium" badge="Challengeable">
            <strong>"You should have avoided the pothole."</strong> Contributory negligence is rarely 
            a complete defence. Drivers are entitled to expect safe roads.
          </SeverityLevel>

          <SeverityLevel level="low" badge="Weak argument">
            <strong>"Insufficient evidence."</strong> Often a lazy response. If you provided photos, 
            quotes, and location data, push back and ask exactly what's missing.
          </SeverityLevel>

          <SeverityLevel level="low" badge="Very weak">
            <strong>"Weather conditions caused the damage."</strong> Unless there was an ice storm 
            minutes before your incident, this is usually not credible.
          </SeverityLevel>
        </div>
      </GuideDarkSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        }
        title="How to Respond"
      >
        <GuideGrid cols={2}>
          <GuideCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 21h7a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v11m0 5l4.879-4.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242z" />
              </svg>
            }
            title="Request Their Evidence"
          >
            Ask for their inspection records,{' '}
            <Link to="/guides/prior-reports-explained" className={inlineLink}>
              prior reports data
            </Link>
            , and the specific basis for rejection. They must provide this.
          </GuideCard>
          <GuideCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            title="Counter Their Arguments"
          >
            Write back addressing each point. If you have prior reports, new photos, or other evidence, include it.
          </GuideCard>
          <GuideCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            title="Set a Deadline"
          >
            Give them 14-28 days to reconsider. State that if not resolved, you will proceed to court.
          </GuideCard>
          <GuideCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
              </svg>
            }
            title="Escalate to Court"
          >
            If they don't budge, file in{' '}
            <Link to="/guides/small-claims-court-guide" className={inlineLink}>
              small claims court
            </Link>
            . Many councils settle once they see you're serious.
          </GuideCard>
        </GuideGrid>
      </GuideSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        }
        title="When Rejection Might Be Valid"
      >
        <p className="mb-4">
          Sometimes rejections are legitimate. Consider whether escalation is worthwhile if:
        </p>
        <ul className="space-y-2 mb-4">
          <li className="flex items-start gap-2">
            <svg className="w-5 h-5 text-surface-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="text-surface-700">The road really was private (car park, unadopted estate road)</span>
          </li>
          <li className="flex items-start gap-2">
            <svg className="w-5 h-5 text-surface-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="text-surface-700">Your evidence is genuinely weak (no photos, no proof of damage)</span>
          </li>
          <li className="flex items-start gap-2">
            <svg className="w-5 h-5 text-surface-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="text-surface-700">The defect was genuinely very minor (hairline crack, not a pothole)</span>
          </li>
          <li className="flex items-start gap-2">
            <svg className="w-5 h-5 text-surface-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="text-surface-700">The claim amount is so small it's not worth the court time</span>
          </li>
        </ul>
        <GuideTip variant="info">
          If you're unsure whether your rejection is challengeable, see{' '}
          <Link to="/guides/small-claims-court-guide" className={inlineLink}>
            the Small Claims Court guide
          </Link>
          {' '}to understand the process and costs of escalation, and{' '}
          <Link to="/guides/what-to-expect-at-hearing" className={inlineLink}>
            what to expect at a hearing
          </Link>
          {' '}if it goes that far.
        </GuideTip>
      </GuideSection>
      <GuideTip variant="info" title="Use a records-first review">
        The{' '}
        <Link to="/guides/pothole-claim-rejected-next-steps" className={inlineLink}>
          rejection next-steps guide
        </Link>{' '}
        explains how to separate each reason, request the supporting records and identify when
        regulated legal advice may be appropriate.
      </GuideTip>
    </GuideLayout>
  );
}
